import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { useContainerBilledAsOffer, useContainerFollowUpOffer } from '@Hooks/Orders';
import { type ApiOrder, EnumProcessState } from '@Model';
import { EditFrameMode, type IEditFrame } from '@synqo/frontend-component-library';
import { useMemo } from 'react';
import {
    acceptsNewContainerActions,
    type SiteCardActions,
    stockCountKey,
    useContainerPlanning,
    useContainersAtSite,
    useContainerTypeDetails,
} from '../Containers';

// @ai-generated 2026-10-22T10:53:17+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function useOrderContainers(
    frame: IEditFrame<ApiOrder>,
    embedded: boolean,
    openOrder: (orderId: string) => void,
) {
    const order = frame.data;
    const hasPermission = useHasPermission();
    const requestUpdateState = frame.requestUpdateState;

    const containersAtSite = useContainersAtSite({
        siteId: order.site.address.foreign_key,
        customerId: order.customer.foreign_key,
        containerActions: order.container_actions,
        savedAt: order.modified_at,
        enabled: !embedded,
        completedAt: order.completed_at,
        orderNumber: order.number,
    });

    const planningTarget = useMemo(
        () => ({ kind: 'order' as const, id: order.id, requestUpdateState }),
        [order.id, requestUpdateState],
    );
    const planning = useContainerPlanning(planningTarget);

    useContainerFollowUpOffer(openOrder);
    useContainerBilledAsOffer(order.id, requestUpdateState);

    const containerTypes = useContainerTypeDetails(
        useMemo(
            () => containersAtSite.standing.map((entry) => entry.container_type.foreign_key),
            [containersAtSite.standing],
        ),
    );
    const standingCount = useMemo(
        () =>
            countAlike(
                containersAtSite.standing.map((entry) =>
                    stockCountKey(entry.container_type.foreign_key, entry.content.foreign_key),
                ),
            ),
        [containersAtSite.standing],
    );
    const knownSite = useMemo(
        () =>
            containersAtSite.stockKnown
                ? { standing: containersAtSite.standing, planned: containersAtSite.planned }
                : undefined,
        [containersAtSite.stockKnown, containersAtSite.standing, containersAtSite.planned],
    );
    const siteCardActions = siteCardActionsOf(
        order.process_state,
        frame.mode === EditFrameMode.View,
        hasPermission(Permission.AuftragsbearbeitungContainerActionsUpdate),
    );

    return { containersAtSite, planning, containerTypes, standingCount, knownSite, siteCardActions };
}

export type OrderContainers = ReturnType<typeof useOrderContainers>;

// @ai-generated 2026-10-15T12:08:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
function siteCardActionsOf(
    processState: EnumProcessState,
    isInViewMode: boolean,
    canPlanContainers: boolean,
): SiteCardActions {
    if (!acceptsNewContainerActions(processState, !isInViewMode)) {
        return 'none';
    }

    if (!canPlanContainers) {
        return 'none';
    }

    return isInViewMode ? 'locked' : 'edit';
}

// @ai-generated 2026-10-19T12:51:29+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
function countAlike(keys: string[]): ReadonlyMap<string, number> {
    const counts = new Map<string, number>();
    for (const key of keys) {
        counts.set(key, (counts.get(key) ?? 0) + 1);
    }

    return counts;
}
